import React from "react";
import glamorous from "glamorous";
import moment from "moment";
import _ from "lodash";

import { MadChicletCSS as Chiclet } from "../../components/chiclets";
import {
  textColorForExceptionType,
  iconForExceptionType
} from "../exceptions/ExceptionLink";
import { getArrivalWindowText } from "../shipment-detail/ArrivalWindow";

const StatusCellRenderer = props => {
  const { status, exception } = props.value;

  return (
    <glamorous.Div css={{ display: "flex", flexDirection: "column" }}>
      <glamorous.Div css={{ fontWeight: "bold", marginLeft: "0.25em" }}>
        {status}
      </glamorous.Div>
      <glamorous.Div
        css={{
          color: textColorForExceptionType(exception),
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          marginTop: "0.5em"
        }}
      >
        <glamorous.Div css={{ fontSize: "large" }}>
          {exception && iconForExceptionType(exception)}
        </glamorous.Div>
        <glamorous.Div css={{ marginLeft: "0.25em" }}>
          {exception}
        </glamorous.Div>
      </glamorous.Div>
    </glamorous.Div>
  );
};

const ModeCellRenderer = props => {
  const { mode, exception, carrier } = props.value;

  return (
    <glamorous.Div display="flex" flexDirection="column">
      <glamorous.Div marginLeft="0.25em" marginBottom="0.5em">
        {carrier}
      </glamorous.Div>
      <glamorous.Div
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
      >
        <glamorous.Div marginLeft="0.25em">
          <Chiclet
            shipmentMode={mode}
            activeException={exception}
            width={25}
            height={25}
          />
        </glamorous.Div>
        <glamorous.Div marginLeft="0.5em" marginRight="0.5em" width="100%">
          {mode}
        </glamorous.Div>
      </glamorous.Div>
    </glamorous.Div>
  );
};

const NullDatetimeRow = label => {
  return (
    <glamorous.Div
      css={{ color: "#999", display: "flex", flexDirection: "column" }}
    >
      <glamorous.Div
        fontWeight="bold"
        fontSize="small"
        textDecoration="underline"
      >
        {label}
      </glamorous.Div>
      N/A
    </glamorous.Div>
  );
};

function DatetimeRow(label, windowTimeText, windowDateText) {
  if (windowTimeText)
    return (
      <glamorous.Div
        display="flex"
        flexDirection="column"
        alignItems="left"
        fontStyle="normal"
        fontWeight="bold"
      >
        <glamorous.Div
          fontWeight="bold"
          fontSize="small"
          textDecoration="underline"
        >
          {label}
        </glamorous.Div>
        {windowTimeText}
        <glamorous.Div fontWeight="normal">{windowDateText}</glamorous.Div>
      </glamorous.Div>
    );
}

const RowForWindow = (label, earliest, latest) => {
  if (!(earliest || latest)) {
    return NullDatetimeRow(label);
  }
  const text = getArrivalWindowText({
    earliest_arrival_datetime: earliest,
    latest_arrival_datetime: latest
  });
  return DatetimeRow(label, text.windowTimeText, text.windowDateText);
};

const DateTimeWindowCellRenderer = props => {
  const { earliestExpected, latestExpected, actual } = props.value;
  const expectedRow = RowForWindow(
    "Scheduled",
    earliestExpected,
    latestExpected
  );
  const actualRow = RowForWindow("Actual", actual);

  return (
    <glamorous.Div display="flex" flexDirection="column" alignItems="left">
      {expectedRow}
      <glamorous.Div height="0.5em" />
      {actualRow}
    </glamorous.Div>
  );
};

const getInterchangeRow = references => {
  if (references) {
    // Filter down to ETAI references in the future
    const interchangeRefs = references
      .filter(
        r =>
          _.includes(["ETAI"], r.qualifier) &&
          moment(_.split(r.value, "|")[3], "YYYY-MM-DD-HH:mm:SSS") > moment()
      )
      .sort((r1, r2) => {
        const etai1 = _.split(r1.value, "|");
        const eta1 = moment(etai1[3], "YYYY-MM-DD-HH:mm:SSS");
        const etai2 = _.split(r2.value, "|");
        const eta2 = moment(etai2[3], "YYYY-MM-DD-HH:mm:SSS");

        return eta1.valueOf() - eta2.valueOf();
      });

    if (interchangeRefs.length > 0) {
      const etai = _.split(interchangeRefs[0].value, "|");
      let loc = `${etai[0]}, ${etai[1]}`;
      let eta = etai[3];
      let tz = etai[4];

      return (
        <glamorous.Div css={{ marginTop: "0.5em" }}>
          <glamorous.Div
            css={{
              fontSize: "small",
              textDecoration: "underline",
              fontWeight: "bold"
            }}
          >
            Next Interchange
          </glamorous.Div>
          <glamorous.Div>
            <glamorous.Span css={{ marginTop: "0.5em", fontWeight: "bold" }}>
              {moment(eta, "YYYY-MM-DD-HH:mm:SSS")
                .utcOffset(tz)
                .local()
                .format("HH:mm")}
            </glamorous.Span>
            <br />
            <span>
              {moment(eta, "YYYY-MM-DD-HH:mm:SSS")
                .utcOffset(tz)
                .local()
                .format("l")}
            </span>
          </glamorous.Div>
          <glamorous.Div css={{ marginTop: "0.5em", fontSize: "small" }}>
            {loc}
          </glamorous.Div>
        </glamorous.Div>
      );
    }
  }

  return null;
};

const LocationCellRenderer = props => {
  const { name, city, state, postalCode, code, references } = props.value;

  return (
    <glamorous.Div display="flex" flexDirection="column" alignItems="left">
      <glamorous.Div css={{ fontWeight: "bold" }}>{code}</glamorous.Div>
      <glamorous.Div css={{ marginBottom: "0.5em" }}>{name}</glamorous.Div>
      <glamorous.Div fontSize="small">
        {`${city}, ${state}, ${postalCode}`}
      </glamorous.Div>
      {getInterchangeRow(references)}
    </glamorous.Div>
  );
};

const IDCellRenderer = props => {
  return (
    <glamorous.Div css={{ fontWeight: "bold" }}>{props.value}</glamorous.Div>
  );
};

export const getColumns = lads => {
  return [
    {
      Header: "Shipment ID",
      id: "creator_shipment_id",
      accessor: "creator_shipment_id",
      Cell: IDCellRenderer
    },
    {
      Header: "Status",
      id: "current_status_ng",
      accessor: d => {
        return {
          status: d.current_status_ng,
          exception: d.active_exceptions_ng
        };
      },
      Cell: StatusCellRenderer,
      getProps: (state, rowInfo) => ({
        style: {
          whiteSpace: "unset"
        }
      })
    },
    {
      Header: "Carrier",
      id: "carrier_name",
      accessor: d => {
        return {
          mode: d.mode_name,
          exception: d.active_exceptions_ng,
          carrier: d.carrier_name
        };
      },
      getProps: (state, rowInfo) => ({
        style: {
          whiteSpace: "unset"
        }
      }),
      Cell: ModeCellRenderer
    },
    {
      Header: "Origin",
      id: "origin_name",
      Cell: LocationCellRenderer,
      accessor: d => {
        return {
          name: d.origin_name,
          city: d.origin_city,
          state: d.origin_state,
          postalCode: d.origin_postal_code,
          code: d.origin_location_code,
          references: d.shipment_references
        };
      },
      getProps: (state, rowInfo) => ({
        style: {
          whiteSpace: "unset"
        }
      })
    },
    {
      Header: "Destination",
      id: "destination_name",
      Cell: LocationCellRenderer,
      accessor: d => {
        return {
          name: d.destination_name,
          city: d.destination_city,
          state: d.destination_state,
          postalCode: d.destination_postal_code,
          code: d.destination_location_code
        };
      },
      getProps: (state, rowInfo) => ({
        style: {
          whiteSpace: "unset"
        }
      })
    },
    // DEV-256: for the expected arrival times (at origin and destination), the
    // field ids are still origin_earliest_arrival and destination_earliest_arrival.
    // However, we previously used "d.origin_earliest_arrival" and
    // "d.destination_earliest_arrival" as the data sources for these fields.
    // The ask here is to use latest time instead.
    {
      Header: "Pickup",
      id: "origin_earliest_arrival",
      accessor: d => {
        return {
          earliestExpected: d.origin_earliest_arrival,
          latestExpected: d.origin_latest_arrival,
          actual: d.origin_actual_arrival
        };
      },
      Cell: DateTimeWindowCellRenderer
    },
    {
      Header: "Delivery",
      id: "destination_earliest_arrival",
      accessor: d => {
        return {
          earliestExpected: d.destination_earliest_arrival,
          latestExpected: d.destination_latest_arrival,
          actual: d.destination_actual_arrival
        };
      },
      Cell: DateTimeWindowCellRenderer
    }
  ];
};



// WEBPACK FOOTER //
// ./src/modules/shipment-search/ShipmentSearchColumns.js